import {useEffect, useMemo} from 'react'
import {ID} from '../../_metronic/helpers'
import {CForm, FieldProps} from '../Form'
import {CView} from '../view/View'
import {useQuery} from '@tanstack/react-query'
import {v2_get_request_api} from '../../api/get'
import {Header} from './ModalHeader'
import { X } from 'lucide-react'

type KModalProps = {
  model: string
  title: string
  setItemIdForUpdate: (id: ID) => void
  itemIdForUpdate: ID | undefined
  setItemIdToView: (id: ID | undefined) => void
  itemIdToView: ID | undefined
  fields: FieldProps[] | (() => FieldProps[])
  data?: Record<string, any>[]
  update?: (data: any) => Promise<any>
  create?: (data: any) => Promise<any>
  delete?: (id: ID) => Promise<any>
  refetch?: () => void
}

export const KModal = (props: KModalProps) => {
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  const id = useMemo(
    () => props.itemIdForUpdate || props.itemIdToView,
    [props.itemIdForUpdate, props.itemIdToView]
  )

  const {
    isInitialLoading: isLoading,
    data,
    error,
  } = useQuery([props.model + '/' + id], v2_get_request_api, {
    enabled: !!id,
    onError: () => {
      props.setItemIdForUpdate(undefined)
      props.setItemIdToView(undefined)
    },
    placeholderData: id ? () => props.data?.find((d) => d.id === id) : undefined,
  })

  if (error) {
    props.setItemIdForUpdate(undefined)
    props.setItemIdToView(undefined)
    return null
  }

  const fieldLength =
    typeof props.fields === 'function' ? props.fields().length : props.fields.length

  return (
    <>
      {/* Modal Overlay */}
      <div className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50'>
        <div
          className={`relative bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4 ${
            fieldLength > 3 ? 'lg:max-w-4xl' : 'lg:max-w-2xl'
          }`}
          role='dialog'
          tabIndex={-1}
        >
          <button
            onClick={() => {
              props.setItemIdForUpdate(undefined)
              props.setItemIdToView(undefined)
            }}
            className='absolute top-4 right-4 text-white bg-[#008080] hover:bg-[#006666] rounded-full p-2 focus:outline-none shadow-md z-20'
            aria-label='Close modal'
          >
            <X className='w-5 h-5' />
          </button>

          {/* Floating Header */}
          <div className='flex justify-center'>
            <div className='w-[70%] -mt-10 mb-4 bg-primary text-white text-center py-3 px-4 rounded-xl shadow-md z-10'>
              <Header
                model={props.model}
                title={props.title}
                setItemIdForUpdate={props.setItemIdForUpdate}
                itemIdForUpdate={props.itemIdForUpdate}
                setItemIdToView={props.setItemIdToView}
                itemIdToView={props.itemIdToView}
                deleteAction={props.delete}
                refetch={props.refetch}
              />
            </div>
          </div>

          {/* Modal Body with padding-top to push content below header */}
          <div className='max-h-[75vh] overflow-y-auto px-6 py-6'>
            {props.itemIdToView ? (
              <CView isLoading={isLoading} data={data} fields={props.fields} />
            ) : props.create || props.update ? (
              <CForm
                isLoading={isLoading}
                data={data}
                update={props.update}
                create={props.create}
                setItemIdForUpdate={props.setItemIdForUpdate}
                refetch={props.refetch}
                fields={props.fields}
       
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
